import type { AppLanguage } from "@/i18n";
import { apiRequest } from "./client";
import type { SignupAttribution } from "@/lib/signup-attribution";

export interface User {
  id: string;
  email: string;
  name: string;
  timezone: string;
  is_email_verified: boolean;
  has_password: boolean;
  date_joined: string;
  onboarding_step: string | null;
  desktop_onboarding_version: number;
  seen_tips: string[];
  subscription_tier: "free" | "pro" | "max";
  agent_avatar_style: "glass" | "icons";
  /** The app language last pushed by a desktop app; null before the first. */
  language: AppLanguage | null;
  /** Read-only server policy; older backends may omit it. */
  analytics_excluded?: boolean;
}

interface AuthDetail {
  detail: string;
}

export function register(data: {
  name: string;
  email: string;
  password1: string;
  password2: string;
  newsletter_consent?: boolean;
  signup_attribution?: SignupAttribution;
  attribution_token?: string;
}): Promise<AuthDetail> {
  return apiRequest("/api/v1/auth/register/", {
    method: "POST",
    body: data,
    silent: true,
  });
}

export function login(data: {
  email: string;
  password: string;
}): Promise<AuthDetail> {
  return apiRequest("/api/v1/auth/login/", {
    method: "POST",
    body: data,
    silent: true,
  });
}

export function logout(): Promise<AuthDetail> {
  return apiRequest("/api/v1/auth/logout/", { method: "POST" });
}

export function getUser(): Promise<User> {
  return apiRequest("/api/v1/auth/user/", { silent: true });
}

/** Tells the account which language the app shows, so agents reply in it. */
export function updateAppLanguage(language: AppLanguage): Promise<unknown> {
  return apiRequest("/api/v1/auth/language/", {
    method: "PUT",
    body: { language },
    silent: true,
  });
}

export function updateUser(data: {
  name?: string;
  timezone?: string;
  agent_avatar_style?: "glass" | "icons";
}): Promise<User> {
  return apiRequest("/api/v1/auth/user/", {
    method: "PUT",
    body: data,
    silent: true,
  });
}

export function requestPasswordReset(email: string): Promise<AuthDetail> {
  return apiRequest("/api/v1/auth/password/reset/", {
    method: "POST",
    body: { email },
    silent: true,
  });
}

export function confirmPasswordReset(data: {
  uid: string;
  token: string;
  new_password1: string;
  new_password2: string;
}): Promise<AuthDetail> {
  return apiRequest("/api/v1/auth/password/reset/confirm/", {
    method: "POST",
    body: data,
    silent: true,
  });
}

export interface VerifyEmailResponse {
  detail: string;
  signed_in: boolean;
}

export function verifyEmail(key: string): Promise<VerifyEmailResponse> {
  return apiRequest("/api/v1/auth/email/verify/", {
    method: "POST",
    body: { key },
    silent: true,
  });
}

export function resendVerificationEmail(email: string): Promise<AuthDetail> {
  return apiRequest("/api/v1/auth/email/resend/", {
    method: "POST",
    body: { email },
    silent: true,
  });
}

export function changePassword(data: {
  old_password?: string;
  new_password1: string;
  new_password2: string;
}): Promise<AuthDetail> {
  return apiRequest("/api/v1/auth/password/change/", {
    method: "POST",
    body: data,
    silent: true,
  });
}

/* ---------- Notification Preferences ---------- */

export interface NotificationPreferences {
  email_weekly_digest: boolean;
  email_agent_completed_task: boolean;
  email_collaborator_activity: boolean;
  inapp_mentions: boolean;
  inapp_agent_updates: boolean;
  inapp_project_invites: boolean;
}

interface ApiSuccess<T> {
  status: "success";
  data: T;
}

export async function getNotificationPreferences(): Promise<NotificationPreferences> {
  const res = await apiRequest<ApiSuccess<NotificationPreferences>>(
    "/api/v1/auth/notifications/",
  );
  return res.data;
}

export async function updateNotificationPreferences(
  updates: Partial<NotificationPreferences>,
): Promise<NotificationPreferences> {
  const res = await apiRequest<ApiSuccess<NotificationPreferences>>(
    "/api/v1/auth/notifications/",
    { method: "PUT", body: updates },
  );
  return res.data;
}

/* ---------- Account Deletion ---------- */

export async function deleteAccount(): Promise<void> {
  await apiRequest("/api/v1/auth/delete-account/", {
    method: "DELETE",
    body: { confirm: true },
  });
}

/* ---------- Profile ---------- */

export interface UserProfile {
  id: string;
  email: string;
  name: string;
  timezone: string;
  max_parallel_agents: number;
  agent_avatar_style: "glass" | "icons";
}

export async function getProfile(): Promise<UserProfile> {
  const res = await apiRequest<ApiSuccess<UserProfile>>(
    "/api/v1/auth/profile/",
  );
  return res.data;
}

export async function updateMaxParallelAgents(
  value: number,
): Promise<UserProfile> {
  const res = await apiRequest<ApiSuccess<UserProfile>>(
    "/api/v1/auth/profile/",
    {
      method: "PATCH",
      body: { max_parallel_agents: value },
    },
  );
  return res.data;
}
